/**
 * Client-side validation, derived entirely from the schema.
 *
 * This is a courtesy layer and nothing more. Every rule below is also enforced
 * by the API and by a CHECK constraint underneath it — the value of running it
 * here is that a bad value is refused *in front of the person making the
 * change*, which the design doc calls the single biggest day-to-day win of
 * moving off environment variables. Never treat it as a control: a token holder
 * with curl never runs this code.
 */

import type { ConfigRow, ConfigTypeSchema, FieldSchema, KeyFieldSchema } from '../api/types'
import { isBlank } from './format'
import { splitStatusNote } from './statusNote'

export type FieldErrors = Record<string, string>

export function validateKey(keyField: KeyFieldSchema, value: string): string | null {
  const trimmed = value.trim()
  if (!trimmed) return `${keyField.label} is required.`
  if (keyField.max_len && trimmed.length > keyField.max_len) {
    return `At most ${keyField.max_len} characters.`
  }
  if (keyField.pattern && !new RegExp(keyField.pattern).test(trimmed)) {
    return keyField.pattern_message ?? 'Not a valid value.'
  }
  if (keyField.options && !keyField.options.some((o) => o.value === trimmed)) {
    return 'Choose one of the listed values.'
  }
  return null
}

/** Applies the key field's declared casing. Consent keys are matched `.upper()`. */
export function transformKey(keyField: KeyFieldSchema, value: string): string {
  if (keyField.transform === 'upper') return value.toUpperCase()
  if (keyField.transform === 'lower') return value.toLowerCase()
  return value
}

function validateField(field: FieldSchema, value: unknown): string | null {
  if (isBlank(value)) {
    // `nullable: false` with no value is only an error on a field the user can
    // actually set; server-maintained columns are filled in by the handler.
    return field.editable && !field.nullable && field.type !== 'bool'
      ? `${field.label} is required.`
      : null
  }

  switch (field.type) {
    case 'text': {
      const text = String(value)
      if (field.max_len && text.length > field.max_len) {
        return `At most ${field.max_len} characters.`
      }
      if (field.pattern && !new RegExp(field.pattern).test(text)) {
        return field.pattern_message ?? 'Not a valid value.'
      }
      return null
    }
    case 'enum':
      return field.options.some((o) => o.value === value) ? null : 'Pick one of the options.'
    case 'status_note': {
      const { status, note } = splitStatusNote(field, value)
      if (!status) return 'Choose a status.'
      if (note.trim().length > field.note_max_len) {
        return `The comment can be at most ${field.note_max_len} characters.`
      }
      return null
    }
    case 'int':
    case 'minute_of_day': {
      const num = Number(value)
      if (!Number.isFinite(num)) return 'Must be a number.'
      if (field.min !== undefined && num < field.min) return `Must be at least ${field.min}.`
      if (field.max !== undefined && num > field.max) return `Must be at most ${field.max}.`
      return null
    }
    default:
      return null
  }
}

/**
 * The `implies` rules, applied as the user types.
 *
 * `exception_forward = 0 OR exception_tagging = 1` is a CHECK constraint, so a
 * form that let someone tick forwarding without tagging would collect a change
 * only to have SQL refuse it. Ticking it here instead makes the implication
 * visible at the moment it is created.
 */
export function applyImplications(schema: ConfigTypeSchema, values: ConfigRow): ConfigRow {
  let next = values
  for (const rule of schema.rules ?? []) {
    if (rule.kind !== 'implies') continue
    if (next[rule.when.field] === rule.when.equals) {
      next = { ...next, ...rule.set }
    }
  }
  return next
}

/** True when a field is required *right now*, given the rest of the row. */
export function isRequiredNow(schema: ConfigTypeSchema, field: FieldSchema, values: ConfigRow): boolean {
  if (field.editable && !field.nullable && field.type !== 'bool') return true
  return (schema.rules ?? []).some(
    (rule) =>
      rule.kind === 'required_when' &&
      values[rule.when.field] === rule.when.equals &&
      rule.fields.includes(field.name),
  )
}

export function validateRow(schema: ConfigTypeSchema, values: ConfigRow): FieldErrors {
  const errors: FieldErrors = {}

  for (const field of schema.fields) {
    if (!field.editable) continue
    const error = validateField(field, values[field.name])
    if (error) errors[field.name] = error
  }

  for (const rule of schema.rules ?? []) {
    if (rule.kind === 'required_when') {
      if (values[rule.when.field] !== rule.when.equals) continue
      for (const name of rule.fields) {
        if (isBlank(values[name])) {
          errors[name] = rule.message ?? 'Required.'
        }
      }
    }

    if (rule.kind === 'range_pair') {
      const start = values[rule.start]
      const end = values[rule.end]
      const startSet = !isBlank(start)
      const endSet = !isBlank(end)
      if (startSet !== endSet) {
        errors[startSet ? rule.end : rule.start] = 'Set both ends of the window, or neither.'
      } else if (startSet && endSet && Number(start) >= Number(end)) {
        errors[rule.end] = rule.message ?? 'Must be after the opening time.'
      }
    }
  }

  return errors
}
